import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { coachAPI, curriculumAPI } from '../../services/api';
import CoachLayout from './CoachLayout';
import StatCard from '../../../../shared/components/StatCard';
import DailyReviewComplianceCard from '../../components/adaptive/DailyReviewComplianceCard';

export default function CoachDashboardPage() {
  const { learner } = useAuth();
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      coachAPI.listLearners().then(({ data }) => data.learners || []).catch(() => []),
      coachAPI.listTutorials().then(({ data }) => data.tutorials || []).catch(() => []),
      curriculumAPI.listDomains().then(({ data }) => data.domains || []).catch(() => []),
    ])
      .then(([l, t, d]) => {
        setLearners(l);
        setTutorials(t);
        setDomains(d);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const publishedTutorials = tutorials.filter((t) => t.status === 'published').length;
  const draftTutorials = tutorials.filter((t) => t.status === 'draft').length;

  return (
    <CoachLayout title={`Welcome back, ${learner?.display_name} 👋`}>
      {error && <div className="coach-error">⚠ {error}</div>}

      {loading ? (
        <div className="coach-loading">Loading dashboard…</div>
      ) : (
        <>
          {/* ── Stats row ──────────────────────────────────────────────── */}
          <div className="coach-cards">
            <StatCard icon="👥" value={learners.length} label="Assigned Learners" />
            <StatCard icon="📝" value={tutorials.length} label="Total Lessons" />
            <StatCard icon="✅" value={publishedTutorials} label="Published" />
            <StatCard icon="🗒️" value={draftTutorials} label="Drafts" />
            <StatCard icon="🏟️" value={domains.length} label="Active Domains" />
          </div>

          {/* ── Daily Review Compliance ─────────────────────────────────── */}
          {/* Coach-only: shows FSRS review compliance per domain, at-risk
              learners, and auto-refreshes every 5 minutes. */}
          <DailyReviewComplianceCard
            domains={[
              'all',
              ...domains.map((d) => d.domain_id).filter(Boolean),
            ]}
            initialDomain="all"
            onLearnerClick={(learnerId) => navigate(`/coach/learners?highlight=${encodeURIComponent(learnerId)}`)}
          />

          {/* ── Quick actions ───────────────────────────────────────────── */}
          {/* Per-domain "Manage <X> Curriculum" buttons replace the
              hardcoded football one — render one per entitled domain
              so AI Coach gets ai_orchestrator + ai_entrepreneur while
              Coach Casey gets only football. Falls back to a generic
              learners-only quick-action set when the coach is in no
              domains (e.g. before entitlements seed). */}
          <div className="coach-section">
            <div className="coach-section-header">
              <h2 className="coach-section-title">Quick Actions</h2>
            </div>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 12 }}>
              <button className="btn btn-primary" onClick={() => navigate('/coach/tutorials')}>
                📝 New Lesson
              </button>
              {domains.map((d) => (
                <button
                  key={`qa-${d.domain_id}`}
                  className="btn btn-outline"
                  onClick={() => navigate(`/coach/curriculum/${d.domain_id}`)}
                >
                  🏟️ Manage {d.display_name || d.domain_id}
                </button>
              ))}
              <button className="btn btn-outline" onClick={() => navigate('/coach/learners')}>
                👥 View My Learners
              </button>
            </div>
          </div>

          {/* ── Recent tutorials ────────────────────────────────────────── */}
          <div className="coach-section">
            <div className="coach-section-header">
              <h2 className="coach-section-title">Recent Lessons</h2>
              <button className="btn-link" onClick={() => navigate('/coach/tutorials')}>View all →</button>
            </div>

            {tutorials.length === 0 ? (
              <div className="coach-empty">
                <span className="coach-empty-icon">📝</span>
                <div className="coach-empty-title">No lessons yet</div>
                <div className="coach-empty-desc">Create your first lesson to get started.</div>
              </div>
            ) : (
              <table className="coach-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Domain</th>
                    <th>Difficulty</th>
                    <th>Status</th>
                    <th>Segments</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tutorials.slice(0, 8).map((t) => (
                    <tr key={t.tutorial_id}>
                      <td style={{ fontWeight: 600 }}>{t.title}</td>
                      <td className="text-muted text-sm">{t.domain_id}</td>
                      <td>
                        <span className={`badge badge-${t.difficulty}`}>{t.difficulty}</span>
                      </td>
                      <td>
                        <span className={`badge badge-${t.status}`}>{t.status}</span>
                      </td>
                      <td className="text-muted text-sm">{t.segments?.length ?? 0}</td>
                      <td>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => navigate(`/coach/tutorials/${t.tutorial_id}`)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── My learners ─────────────────────────────────────────────── */}
          <div className="coach-section">
            <div className="coach-section-header">
              <h2 className="coach-section-title">My Learners</h2>
              <button className="btn-link" onClick={() => navigate('/coach/learners')}>View all →</button>
            </div>

            {learners.length === 0 ? (
              <div className="coach-empty">
                <span className="coach-empty-icon">👥</span>
                <div className="coach-empty-title">No learners assigned</div>
                <div className="coach-empty-desc">Learners will appear here once assigned to your roster.</div>
              </div>
            ) : (
              <table className="coach-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Domains</th>
                  </tr>
                </thead>
                <tbody>
                  {learners.slice(0, 6).map((l) => (
                    <tr key={l.learner_id}>
                      <td style={{ fontWeight: 600 }}>{l.display_name}</td>
                      <td className="text-muted text-sm">{l.email}</td>
                      <td className="text-muted text-sm">
                        {(l.enrolled_domains || []).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Domains ─────────────────────────────────────────────────── */}
          {domains.length > 0 && (
            <div className="coach-section">
              <div className="coach-section-header">
                <h2 className="coach-section-title">Domains</h2>
              </div>
              <div className="coach-cards">
                {domains.map((d) => (
                  <div
                    key={d.domain_id}
                    className="coach-stat-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/coach/curriculum/${d.domain_id}`)}
                  >
                    <span className="stat-icon">🏟️</span>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>{d.display_name}</span>
                    <span className="text-muted text-sm">
                      {(d.supported_modes || []).join(' · ') || d.domain_id}
                    </span>
                    <span className="btn-link" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                      Manage Curriculum →
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </CoachLayout>
  );
}
